import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@xxtract/ui';
import {
  ONDERGRENS_GEMIDDELDE,
  bouwCategorieBlokken,
  naamVoorCategorie,
  type CategorieBlok as CategorieBlokGegevens,
  type CategorieRegel,
} from '../productcategorieen';
import type { StapKeuze } from '../stapFilter';
import type { HandlingTimeCategorieBlok } from '../handlingTime.api';

/**
 * Story 34.1 — waar gaat de tijd heen per soort product.
 *
 * Het blok onder de aandachtslijst. Elf regels per stap: de tien categorieën in vaste volgorde, plus
 * *overige categorieën*. Het rekenwerk staat in `productcategorieen.ts`; hier staat alleen hoe het
 * op het scherm komt.
 *
 * Drie dingen die dit onderdeel met opzet NIET doet:
 *
 * - Het velt geen oordeel. Er is geen norm per categorie en dus geen kleur (AC-15). 220 afgeronde
 *   stuks over vijf werkdagen is geen basis voor een drempel die iets rood maakt.
 * - Het filtert niet. Het scherm heeft al een stapfilter; een tweede maakt van een overzicht een
 *   zoekmachine (AC-16).
 * - Het verzint geen naam. De tien namen liggen vast in `productcategorieen.ts` en komen niet uit de
 *   gegevens — de vertaaltabel draagt twintig namen op zeventien codes (AC-2).
 *
 * EEN LEGE REGEL IS INFORMATIE (AC-8). De tien staan er altijd, ook op nul: dat zegt dat er in deze
 * periode niets van dat soort binnenkwam, en dat is iets anders dan een regel die ontbreekt.
 */

export interface CategorieBlokProps {
  /** De blokken zoals de bron ze levert, ongewijzigd. Afwezig zodra een oude bron antwoordt. */
  blokken: HandlingTimeCategorieBlok[] | undefined;
  /** Eén stap, of op "Beide" de twee stappen in hun vaste volgorde: vastleggen vóór valideren. */
  stappen: readonly StapKeuze[];
}

const SLEUTEL = 'handlingTime.categorieblok';

/** De vier kolommen in hun vaste volgorde; één lijst, zodat kop en cel niet uit elkaar lopen. */
const KOLOMMEN = [
  { naam: 'categorie', rechts: false },
  { naam: 'stuks', rechts: true },
  { naam: 'gemiddelde', rechts: true },
  { naam: 'totaal', rechts: true },
] as const;

export function CategorieBlok({ blokken, stappen }: CategorieBlokProps) {
  const { t, i18n } = useTranslation();
  const taal = i18n.language;

  const heelGetal = useMemo(() => new Intl.NumberFormat(taal, { maximumFractionDigits: 0 }), [taal]);
  const eenDecimaal = useMemo(
    () => new Intl.NumberFormat(taal, { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
    [taal],
  );

  const gebouwd = useMemo(() => bouwCategorieBlokken(blokken, stappen), [blokken, stappen]);

  // DE BRON KENT DE UITSPLITSING NOG NIET, en dan zegt het blok dát — het randgeval "antwoord
  // zonder uitsplitsing per stap" uit de spec, en dezelfde lijn die de aandachtslijst met
  // `uitsplitsingMist` volgt. Zwijgen zou het laten samenvallen met "er is niets gemeten", en dat
  // is een uitspraak over de periode in plaats van over de gegevens (codereviewbevinding
  // 31 augustus 2026).
  if (blokken === undefined) {
    return (
      <Card className="p-5" data-testid="handling-time-categorieblok">
        <h2 className="text-sm font-semibold text-[var(--foreground)]">{t(`${SLEUTEL}.titel`)}</h2>
        <p
          className="mt-1 max-w-[46rem] text-xs leading-relaxed text-[var(--foreground-muted)]"
          data-testid="handling-time-categorieblok-geen-uitsplitsing"
        >
          {t(`${SLEUTEL}.geenUitsplitsing`)}
        </p>
      </Card>
    );
  }

  // NIETS GEMETEN IS NIETS TEKENEN. Een leeg kader met elf nullen zou beweren dat er gemeten is en
  // dat het nul was; het echte antwoord is dat er in deze periode niets gemeten is, en dat zegt het
  // scherm al één blok hoger.
  if (gebouwd.length === 0) return null;

  /** De naam van een categorie, of de vaste tekst voor de elfde regel. */
  function naamVoor(regel: CategorieRegel): string {
    return regel.code === null
      ? t(`${SLEUTEL}.overige`)
      : (naamVoorCategorie(regel.code) ?? t(`${SLEUTEL}.overige`));
  }

  /**
   * Het gemiddelde per stuk, of een streepje onder de ondergrens (AC-9).
   *
   * Het streepje is GEEN nul en geen ontbrekende meting: het aantal en de totale tijd staan er wél,
   * alleen de deling ontbreekt. De uitleg eronder zegt waarom, één keer voor het hele blok.
   */
  function alsGemiddelde(regel: CategorieRegel): string {
    if (regel.gemiddeldePerStuk === null) return t(`${SLEUTEL}.geenGemiddelde`);
    return eenDecimaal.format(regel.gemiddeldePerStuk);
  }

  function regelBlok(stap: StapKeuze, regel: CategorieRegel) {
    const sleutel = `handling-time-categorieblok-${stap}-regel-${regel.code ?? 'overige'}`;
    // EEN LEGE REGEL BLIJFT STAAN maar valt terug in kleur: hij draagt informatie, en hij hoort de
    // regels die wél iets zeggen niet weg te drukken.
    const leeg = regel.stuks === 0 && regel.minuten === 0;

    return (
      <TableRow data-leeg={leeg ? 'true' : undefined} data-testid={sleutel} key={regel.code ?? 'overige'}>
        <TableCell
          className={`font-medium ${leeg ? 'text-[var(--foreground-muted)]' : 'text-[var(--foreground)]'}`}
          data-testid={`${sleutel}-naam`}
        >
          {naamVoor(regel)}
        </TableCell>
        <TableCell className="text-right tabular-nums text-[var(--foreground-muted)]" data-testid={`${sleutel}-stuks`}>
          {heelGetal.format(regel.stuks)}
        </TableCell>
        <TableCell className="text-right tabular-nums text-[var(--foreground)]" data-testid={`${sleutel}-gemiddelde`}>
          {alsGemiddelde(regel)}
        </TableCell>
        <TableCell
          className="text-right font-semibold tabular-nums text-[var(--foreground-heading)]"
          data-testid={`${sleutel}-minuten`}
        >
          {t(`${SLEUTEL}.minuten`, { waarde: heelGetal.format(Math.round(regel.minuten)) })}
        </TableCell>
      </TableRow>
    );
  }

  function stapBlok(blok: CategorieBlokGegevens) {
    const sleutel = `handling-time-categorieblok-${blok.stap}`;

    return (
      <section className="mt-4 first:mt-0" data-testid={sleutel} key={blok.stap}>
        {/* De kop staat er ALLEEN op "Beide", precies zoals bij de aandachtslijst: op één gekozen
            stap zegt het filter bovenaan al waar dit blok over gaat. */}
        {gebouwd.length > 1 && (
          <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--foreground-muted)]">
            {t(`handlingTime.stapfilter.${blok.stap}`)}
          </h3>
        )}

        <div className="mt-2 overflow-x-auto">
          <Table data-testid={`${sleutel}-tabel`}>
            <TableHeader>
              <TableRow>
                {KOLOMMEN.map(({ naam, rechts }) => (
                  <TableHead
                    className={`px-3 py-2 text-xs font-semibold uppercase tracking-wide text-[var(--foreground-muted)] ${
                      rechts ? 'text-right' : 'text-left'
                    }`}
                    key={naam}
                    scope="col"
                  >
                    {t(`${SLEUTEL}.${naam}`)}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>{blok.regels.map((regel) => regelBlok(blok.stap, regel))}</TableBody>
          </Table>
        </div>

        {/* DE OPTELLING STAAT ER, en dat is geen sierlijkheid: het blok telt op tot de gemeten tijd
            van DEZE stap, niet tot die van de hele periode. Zonder dit getal is elk percentage dat
            een lezer er zelf uit haalt op de verkeerde noemer gebaseerd (AC-10). */}
        <p className="mt-2 text-xs leading-relaxed text-[var(--foreground-muted)]" data-testid={`${sleutel}-totaal`}>
          {t(`${SLEUTEL}.optelling`, {
            minuten: heelGetal.format(Math.round(blok.totaleMinuten)),
            stuks: heelGetal.format(blok.totaleStuks),
            stap: t(`handlingTime.stapfilter.${blok.stap}`).toLowerCase(),
          })}
        </p>
      </section>
    );
  }

  return (
    <Card className="p-5" data-testid="handling-time-categorieblok">
      <div>
        <h2 className="text-sm font-semibold text-[var(--foreground)]">{t(`${SLEUTEL}.titel`)}</h2>
        <p
          className="mt-1 max-w-[46rem] text-xs text-[var(--foreground-muted)]"
          data-testid="handling-time-categorieblok-ondertitel"
        >
          {t(`${SLEUTEL}.ondertitel`)}
        </p>
      </div>

      {gebouwd.map((blok) => stapBlok(blok))}

      {/* DE UITLEG ONDER HET BLOK, en één keer voor het geheel: op "Beide" zou dezelfde zin anders
          twee keer onder elkaar staan, en dan leest hij als opvulling in plaats van als uitleg —
          precies de bevinding die de aandachtslijst een ronde eerder opleverde. */}
      <p
        className="-mx-5 mt-4 flex items-start gap-2 border-t border-[var(--border-soft)] bg-[var(--background-muted)] px-5 py-3 text-xs leading-relaxed text-[var(--foreground)]"
        data-testid="handling-time-categorieblok-uitleg"
      >
        <span aria-hidden="true" className="mdi mdi-information-outline mt-px flex-none text-sm text-[var(--foreground-muted)]" />
        <span>{t(`${SLEUTEL}.uitleg`, { grens: ONDERGRENS_GEMIDDELDE })}</span>
      </p>
    </Card>
  );
}
